// =============================================================================
// sellingPricesExcel.js
// Excel template export / import for Revenue selling prices (bud_selling_prices).
//
// Wide layout — one row per finished-good SKU (× customer group), a Customer
// Group column, twelve period columns (P1..P12) and a Unit column. Mirrors the
// volume templates so a user can export the current prices, edit in Excel, and
// re-import (overwrite / replace).
//
// The SKU and Customer Group columns carry in-cell dropdowns (data validation)
// backed by a hidden "Lists" sheet. A blank / "(Universal)" Customer Group is
// the COUNTRY RETAIL PRICE (the default for every customer); a row WITH a
// customer group is that group's price, which OVERRIDES THE COUNTRY RETAIL
// PRICE for customers in that group.
//
// Import expands each row into per-period entries
// ({ stock_code, customer_group_code, financial_period, price, unit_of_measure })
// and validates SKUs / groups against the supplied lists, returning
// { rows, errors }.
// =============================================================================
import ExcelJS from 'exceljs';

const DELIM = ' — ';
const NONE = '(Universal)';   // blank customer group => universal price
const BLANK_ROWS = 200;
const PERIODS = 12;

const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeName = (s) => String(s || 'version').replace(/[^a-z0-9_-]+/gi, '_');

const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return '';
  if (typeof v === 'object') return String(v.text ?? v.result ?? '');
  return String(v);
};

const skuLabel = (s) => `${s.stock_code}${DELIM}${s.description ?? ''}`;
const groupLabel = (g) => `${g.customer_group_code}${DELIM}${g.group_name ?? ''}`;

// ---- Export -----------------------------------------------------------------
// `rows` are grid rows: { stock_code, description, unit_of_measure, prices: {p:num} }.
// `skus` is the finished-good list, `groups` the customer-group list (dropdowns).
// `scopeGroup` (a group code or '' for universal) pre-fills the Customer Group
// column so an export of the current scope round-trips back to that scope.
export async function exportSellingPriceTemplate({ versionLabel, skus = [], groups = [], scopeGroup = '', rows = [], blankRows = BLANK_ROWS }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet('Selling Prices', { views: [{ state: 'frozen', ySplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  const skuOpts = skus.map(skuLabel);
  lists.getCell('A1').value = 'SKU';
  skuOpts.forEach((v, i) => { lists.getCell(`A${i + 2}`).value = v; });

  const groupOpts = [NONE, ...groups.map(groupLabel)];
  lists.getCell('B1').value = 'Customer Group';
  groupOpts.forEach((v, i) => { lists.getCell(`B${i + 2}`).value = v; });

  ws.columns = [
    { header: 'SKU', key: 'sku', width: 40 },
    { header: 'Customer Group', key: 'group', width: 30 },
    ...Array.from({ length: PERIODS }, (_, i) => ({ header: `P${i + 1}`, key: `p${i + 1}`, width: 10 })),
    { header: 'Unit', key: 'uom', width: 10 },
  ];
  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  const skuMap = Object.fromEntries(skus.map((s) => [s.stock_code, skuLabel(s)]));
  const grpMap = Object.fromEntries(groups.map((g) => [g.customer_group_code, groupLabel(g)]));
  const scopeLabel = scopeGroup ? (grpMap[scopeGroup] || scopeGroup) : NONE;
  rows.forEach((r, i) => {
    const row = ws.getRow(i + 2);
    row.getCell(1).value = skuMap[r.stock_code] || r.stock_code;
    row.getCell(2).value = scopeLabel;
    for (let p = 1; p <= PERIODS; p++) {
      const v = r.prices?.[p];
      if (v != null && !Number.isNaN(Number(v))) row.getCell(2 + p).value = Number(v);
    }
    row.getCell(PERIODS + 3).value = r.unit_of_measure || '';
  });

  const lastRow = rows.length + blankRows + 1;
  for (let r = 2; r <= lastRow; r++) {
    ws.getCell(`A${r}`).dataValidation = {
      type: 'list', allowBlank: true,
      formulae: [`=Lists!$A$2:$A$${skuOpts.length + 1}`],
      showErrorMessage: true, errorStyle: 'warning',
      error: 'Pick a finished-good SKU from the list.', errorTitle: 'Pick from the list',
    };
    ws.getCell(`B${r}`).dataValidation = {
      type: 'list', allowBlank: true,
      formulae: [`=Lists!$B$2:$B$${groupOpts.length + 1}`],
      showErrorMessage: true, errorStyle: 'warning',
      error: 'Pick a customer group, or (Universal).', errorTitle: 'Pick from the list',
    };
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `Selling_Prices_Template_${safeName(versionLabel)}.xlsx`);
}

// ---- Import -----------------------------------------------------------------
export async function parseSellingPriceTemplate(file, skus = [], groups = []) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet('Selling Prices') || wb.worksheets[0];
  if (!ws) return { rows: [], errors: ['Could not find a worksheet in the file.'] };

  const skuSet = new Set(skus.map((s) => s.stock_code));
  const groupSet = new Set(groups.map((g) => g.customer_group_code));

  const out = [];
  const errors = [];

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    const skuRaw = cellText(row.getCell(1));
    const groupRaw = cellText(row.getCell(2));
    const periodRaw = Array.from({ length: PERIODS }, (_, p) => cellText(row.getCell(p + 3)));
    const uom = cellText(row.getCell(PERIODS + 3)).trim() || null;

    // Skip entirely blank rows.
    if (![skuRaw, groupRaw, ...periodRaw].some((s) => String(s).trim())) return;

    const rowErrs = [];
    const code = codeOf(skuRaw);
    if (!code) rowErrs.push('missing SKU');
    else if (!skuSet.has(code)) rowErrs.push(`unknown SKU "${code}"`);

    // Blank / (Universal) => null group (the universal price).
    const groupTrim = String(groupRaw).trim();
    let customer_group_code = null;
    if (groupTrim && groupTrim !== NONE) {
      customer_group_code = codeOf(groupTrim);
      if (!groupSet.has(customer_group_code)) rowErrs.push(`unknown Customer Group "${customer_group_code}"`);
    }

    const periodEntries = [];
    periodRaw.forEach((raw, p) => {
      const t = String(raw).trim();
      if (!t) return;
      const price = Number(t);
      if (Number.isNaN(price)) rowErrs.push(`P${p + 1} is not a number`);
      else periodEntries.push({ financial_period: p + 1, price });
    });

    if (rowErrs.length) { errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`); return; }
    periodEntries.forEach((pe) => out.push({ stock_code: code, customer_group_code, financial_period: pe.financial_period, price: pe.price, unit_of_measure: uom }));
  });

  if (errors.length) return { rows: [], errors };
  return { rows: out, errors: [] };
}

export default { exportSellingPriceTemplate, parseSellingPriceTemplate };
